import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { getToken, setToken, removeToken, clearTokens } from '../utils/secureStorage';

const AuthContext = createContext();

// Token storage keys
const ACCESS_TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const TOKEN_EXPIRY_KEY = 'token_expiry';
const SESSION_KEY = 'session_id';
const ALL_TOKEN_KEYS = [ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY, TOKEN_EXPIRY_KEY, SESSION_KEY];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshAccessToken = useCallback(async () => {
    const refreshTokenVal = getToken(REFRESH_TOKEN_KEY);
    if (!refreshTokenVal) return false;

    try {
      const response = await api.post('/auth/refresh', { refresh_token: refreshTokenVal });
      const { access_token, expires_in } = response.data;
      setToken(ACCESS_TOKEN_KEY, access_token);
      setToken(TOKEN_EXPIRY_KEY, Date.now() + (expires_in * 1000));
      return true;
    } catch (error) {
      clearTokens(ALL_TOKEN_KEYS);
      setUser(null);
      return false;
    }
  }, []);

  const checkAndRefreshToken = useCallback(async () => {
    const token = getToken(ACCESS_TOKEN_KEY);
    const expiry = getToken(TOKEN_EXPIRY_KEY);
    if (!token) return false;
    if (expiry && Date.now() > (parseInt(expiry) - 5 * 60 * 1000)) {
      return await refreshAccessToken();
    }
    return true;
  }, [refreshAccessToken]);

  const fetchUser = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          try {
            const response = await api.get('/auth/me');
            setUser(response.data);
            return;
          } catch (e) {
            // Still failed after refresh
          }
        }
      }
      clearTokens(ALL_TOKEN_KEYS);
      setUser(null);
    }
  }, [refreshAccessToken]);

  useEffect(() => {
    const initAuth = async () => {
      const token = getToken(ACCESS_TOKEN_KEY);
      if (token) {
        const valid = await checkAndRefreshToken();
        if (valid) {
          await fetchUser();
        }
      }
      setLoading(false);
    };

    initAuth();

    const refreshInterval = setInterval(async () => {
      if (getToken(ACCESS_TOKEN_KEY)) {
        await checkAndRefreshToken();
      }
    }, 25 * 60 * 1000);

    return () => clearInterval(refreshInterval);
  }, [checkAndRefreshToken, fetchUser]);

  const login = async (email, password, rememberMe = false) => {
    const response = await api.post('/auth/login', { email, password, remember_me: rememberMe });
    const { access_token, refresh_token, expires_in, user: userData, session_id } = response.data;
    
    setToken(ACCESS_TOKEN_KEY, access_token || response.data.token);
    if (refresh_token) setToken(REFRESH_TOKEN_KEY, refresh_token);
    if (expires_in) setToken(TOKEN_EXPIRY_KEY, Date.now() + (expires_in * 1000));
    if (session_id) setToken(SESSION_KEY, session_id);
    
    setUser(userData);
    return response.data;
  };

  const register = async (name, email, password, phone) => {
    const response = await api.post('/auth/register', { name, email, password, phone });
    const data = response.data;
    
    const token = data.access_token || data.token;
    if (token) setToken(ACCESS_TOKEN_KEY, token);
    if (data.refresh_token) setToken(REFRESH_TOKEN_KEY, data.refresh_token);
    if (data.expires_in) setToken(TOKEN_EXPIRY_KEY, Date.now() + (data.expires_in * 1000));
    if (data.session_id) setToken(SESSION_KEY, data.session_id);
    
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Continue with local logout even if API call fails
    }
    clearTokens(ALL_TOKEN_KEYS);
    setUser(null);
  };

  const changePassword = async (currentPassword, newPassword) => {
    const response = await api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    });
    clearTokens(ALL_TOKEN_KEYS);
    setUser(null);
    return response.data;
  };

  return (
    <AuthContext.Provider value={{ 
      user, login, register, logout, loading, changePassword, refreshAccessToken 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
